import { PeriodType } from '@/lib/api/generated/model';
import type { AppliedLimit, PeriodBounds } from '@/lib/api/generated/model';

const MONTH = new Intl.DateTimeFormat('en', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const DAY = new Intl.DateTimeFormat('en', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * Whether a limit is measured over another period than the one the page is reading
 * (spec 216). A weekly limit read in October is: the month's spend and the week's
 * ratio are then two different things, and the page must not set one beside the other.
 */
export function limitHasOwnPeriod(
  limit: AppliedLimit,
  period: PeriodBounds,
): boolean {
  return limit.period.key !== period.key;
}

/**
 * A period as the end of a sentence: "today", "this week", "this month", or, for one
 * that is not current, "on October 19, 2026", "in the week of October 19, 2026",
 * "in October 2026". A lifetime period is "all time" (spec 256).
 */
export function periodPhrase(bounds: PeriodBounds): string {
  if (bounds.period_type === PeriodType.none) return 'all time';
  if (bounds.is_current) {
    if (bounds.period_type === PeriodType.daily) return 'today';
    if (bounds.period_type === PeriodType.weekly) return 'this week';
    return 'this month';
  }
  const start = new Date(bounds.starts_at);
  if (bounds.period_type === PeriodType.daily) return `on ${DAY.format(start)}`;
  if (bounds.period_type === PeriodType.weekly)
    return `in the week of ${DAY.format(start)}`;
  return `in ${MONTH.format(start)}`;
}

/**
 * How a limit's own spend is said beside a figure for the period being read: "today"
 * for a Daily cap read in October, or `null` when the limit is measured over that same
 * period, whose figure is then already the limit's (spec 237). The service sends the
 * limit's period with its spend, so a closed month comes back as that month: `null`.
 */
export function ownPeriodPhrase(
  limitPeriod: PeriodBounds | null | undefined,
  period: PeriodBounds,
): string | null {
  if (!limitPeriod || limitPeriod.key === period.key) return null;
  return periodPhrase(limitPeriod);
}

/** A limit's own spend and how to say it: "$3.00" and "today". */
export interface OwnPeriodSpend {
  spend: string;
  phrase: string;
}

/**
 * A limit's own spend, when the limit is measured over another period than the one
 * being read; `null` when it is that same period, whose figure is then the limit's, or
 * when the service sent none (an older one) and the period's figure stands (spec 237).
 */
export function ownPeriodSpend(
  limitPeriod: PeriodBounds | null | undefined,
  period: PeriodBounds,
  consumed: string | null | undefined,
): OwnPeriodSpend | null {
  const phrase = ownPeriodPhrase(limitPeriod, period);
  return phrase === null || consumed === null || consumed === undefined
    ? null
    : { spend: consumed, phrase };
}

const MONTH_ONLY = new Intl.DateTimeFormat('en-GB', {
  month: 'long',
  timeZone: 'UTC',
});

/**
 * The period as the end of a sentence (spec 252): "in October" for the current month,
 * "this week", "today", or the dated phrase for a past period; null for all time,
 * which has no name to end on.
 */
export function inPeriod(period: PeriodBounds): string | null {
  if (period.period_type === PeriodType.none) return null;
  if (period.period_type === PeriodType.monthly && period.is_current)
    return `in ${MONTH_ONLY.format(new Date(period.starts_at))}`;
  return periodPhrase(period);
}
